import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gear, Trash } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import ProjectMembersPanel from './ProjectMembersPanel';
import ProjectApiKeysPanel from './ProjectApiKeysPanel';
import ProjectReposPanel from './ProjectReposPanel';
import ProjectAgentSettingsPanel from './ProjectAgentSettingsPanel';
import { useI18n } from '../i18n/I18nContext';

export default function ProjectSettings({ project, users = [], onProjectChange, onProjectsChange }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({
    name: project.name,
    description: project.description || '',
    codebase_context: project.codebase_context || '',
    digest_enabled: !!project.digest_enabled,
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isOwner = user && project.owner_id === user.id;

  useEffect(() => {
    if (open) {
      setMeta({
        name: project.name,
        description: project.description || '',
        codebase_context: project.codebase_context || '',
      });
    }
  }, [open, project]);

  const saveMeta = async (e) => {
    e.preventDefault();
    setSavingMeta(true);
    try {
      const { data } = await api.patch(`/projects/${project.id}`, {
        name: meta.name,
        description: meta.description,
        codebase_context: meta.codebase_context,
        digest_enabled: meta.digest_enabled,
      });
      toast.success(t('settings.saveSuccessToast'));
      onProjectChange && onProjectChange(data);
      onProjectsChange && onProjectsChange();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.saveFailedToast')));
    } finally {
      setSavingMeta(false);
    }
  };

  const performDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success(t('settings.deletedToast'));
      setDeleteDialogOpen(false);
      setOpen(false);
      onProjectsChange && (await onProjectsChange());
      nav('/');
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.deleteFailedToast')));
      throw err;
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('settings.deleteConfirmTitle')}
        description={t('settings.deleteConfirmDescription', { name: project.name })}
        confirmLabel={t('settings.deleteConfirmBtn')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={deleting}
        onConfirm={performDelete}
      />
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="project-settings-btn"
          className="text-xs font-mono text-zinc-600 hover:text-[#0055ff] flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-1.5"
          title={t('settings.title')}
        >
          <Gear size={14} /> {t('settings.button')}
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-200">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="font-display font-black tracking-tight">{t('settings.title')}</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">{t('settings.description')}</DialogDescription>
            </div>
            <div className="flex rounded-sm border border-zinc-200 overflow-hidden text-[11px] font-mono shrink-0 mr-8">
              <button
                type="button"
                data-testid="lang-ru-btn"
                onClick={() => setLocale('ru')}
                className={`px-2 py-1 ${locale === 'ru' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
              >
                RU
              </button>
              <button
                type="button"
                data-testid="lang-en-btn"
                onClick={() => setLocale('en')}
                className={`px-2 py-1 ${locale === 'en' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
              >
                EN
              </button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <form onSubmit={saveMeta} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.nameLabel')}</Label>
                  <Input
                    data-testid="settings-name-input"
                    required
                    value={meta.name}
                    onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                    disabled={!isOwner}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div className="flex items-end">
                  {isOwner && (
                    <Button
                      type="submit"
                      data-testid="settings-save-meta-btn"
                      disabled={savingMeta}
                      className="rounded-sm bg-black text-white hover:bg-zinc-800"
                    >
                      {savingMeta ? t('settings.saving') : t('settings.saveMetaBtn')}
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.descriptionLabel')}</Label>
                <Textarea
                  data-testid="settings-description-input"
                  rows={2}
                  value={meta.description}
                  onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                  disabled={!isOwner}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.codebaseContextLabel')}</Label>
                <p className="text-[11px] text-zinc-500 mt-0.5 mb-1">{t('settings.codebaseContextHint')}</p>
                <Textarea
                  data-testid="settings-codebase-context-input"
                  rows={8}
                  value={meta.codebase_context}
                  onChange={(e) => setMeta({ ...meta, codebase_context: e.target.value })}
                  disabled={!isOwner}
                  placeholder={t('settings.codebaseContextPlaceholder')}
                  className="rounded-sm mt-1 font-mono text-xs"
                />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.digestLabel')}</Label>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{t('settings.digestHint')}</p>
                </div>
                <Switch
                  data-testid="settings-digest-toggle"
                  checked={meta.digest_enabled}
                  onCheckedChange={(v) => setMeta({ ...meta, digest_enabled: v })}
                  disabled={!isOwner}
                  className="mt-1 shrink-0"
                />
              </div>
            </form>

            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3">
              <ProjectReposPanel project={project} onProjectChange={onProjectChange} />
            </div>

            {isOwner && (
              <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3">
                <ProjectAgentSettingsPanel project={project} users={users} onProjectChange={onProjectChange} />
              </div>
            )}

            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3">
              <ProjectApiKeysPanel project={project} />
            </div>

            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{t('settings.membersTitle')}</p>
              <ProjectMembersPanel
                project={project}
                users={users}
                onProjectChange={onProjectChange}
                onProjectsChange={onProjectsChange}
              />
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2 px-6 py-3 border-t border-zinc-200 bg-white">
            {isOwner ? (
              <Button
                type="button"
                data-testid="settings-delete-btn"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleting}
                variant="ghost"
                className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash size={14} className="mr-1" /> {t('settings.deleteBtn')}
              </Button>
            ) : <span />}
            <Button
              type="button"
              data-testid="settings-close-btn"
              onClick={() => setOpen(false)}
              variant="outline"
              className="rounded-sm"
            >
              {t('common.close')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
